import { createFileRoute, Link } from "@tanstack/react-router";

import { useSite } from "@/clients/deflow";
import {
  formatCustomDomainLabel,
  formatSiteDomainSummary,
} from "@/lib/site-domains";
import { SiteSettingsForm } from "@/components/plugin-shared/site-settings-form";
import { SiteStaticDeploy } from "@/components/plugin-shared/site-static-deploy";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/sites/$siteId")({
  component: SiteOverview,
});

function SiteOverview() {
  const { siteId } = Route.useParams();
  const { data: site, error, isLoading } = useSite(siteId);

  if (isLoading) {
    return (
      <main className="flex-1 px-8 py-8">
        <p className="text-sm text-muted-foreground">Loading site…</p>
      </main>
    );
  }

  if (error || !site) {
    return (
      <main className="flex-1 px-8 py-8">
        <p className="text-sm text-destructive">Site unavailable.</p>
        <Link to="/dashboard" className="text-sm underline mt-2 inline-block">
          Back to sites
        </Link>
      </main>
    );
  }

  return (
    <main className="flex-1 px-8 py-8 max-w-2xl">
      <div className="mb-6 space-y-1">
        <Link
          to="/dashboard"
          className="text-xs text-muted-foreground hover:underline"
        >
          ← Sites
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          {site.siteName}
        </h1>
        <p className="text-sm text-muted-foreground">
          {formatSiteDomainSummary(site)}
          {site.origin ? (
            <span className="ml-2 rounded border px-1.5 py-0.5 text-xs">
              {site.origin}
            </span>
          ) : null}
        </p>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="w-full">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deploy">Deploy</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-2 pt-4">
          <p className="text-sm">
            Status:{" "}
            <span className="font-mono text-xs">{site.siteStatus}</span>
          </p>
          <p className="text-sm">
            Version:{" "}
            <span className="font-mono text-xs">{site.siteVersion}</span>
          </p>
          {site.designLinkStatus ? (
            <p className="text-sm">
              Design link:{" "}
              <span className="font-mono text-xs">{site.designLinkStatus}</span>
            </p>
          ) : null}
          {site.customSiteHost ? (
            <p className="text-sm">
              Custom domain:{" "}
              <span className="font-mono text-xs">
                {formatCustomDomainLabel(
                  site.customSiteHost,
                  site.customHostEnabled
                )}
              </span>
            </p>
          ) : null}
        </TabsContent>

        <TabsContent value="deploy">
          <SiteStaticDeploy siteId={siteId} siteOrigin={site.origin} />
        </TabsContent>

        <TabsContent value="settings">
          <SiteSettingsForm siteId={siteId} siteName={site.siteName} />
        </TabsContent>
      </Tabs>
    </main>
  );
}
